import type { AppEntry } from "../registry/apps";

export const QUEST_GOAL = 9000;
export const QUEST_DEADLINE = { year: 2026, month: 11, day: 31 } as const;

export type PaceStatus = "empty" | "behind" | "on_pace" | "ahead" | "complete";

export type Progress = {
  count: number;
  remaining: number;
  percent: number;
  daysLeft: number;
  needPerDay: number;
  actualPerDay: number | null;
  shippedToday: number;
  streak: number;
  paceStatus: PaceStatus;
  live: AppEntry[];
};

export function isLive(app: AppEntry): boolean {
  return app.status === "live" && app.example !== true;
}

export function isPlaceholder(app: AppEntry): boolean {
  return app.example === true || app.status !== "live";
}

export function shippedDay(app: AppEntry): string {
  return app.shippedAt.slice(0, 10);
}

export function startOfLocalDay(d = new Date()): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

export function toDayString(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Days from today through 31 Dec 2026, inclusive. 0 after the deadline. */
export function daysRemainingInclusive(now = new Date()): number {
  const today = startOfLocalDay(now);
  const end = new Date(QUEST_DEADLINE.year, QUEST_DEADLINE.month, QUEST_DEADLINE.day);
  const diff = Math.round((end.getTime() - today.getTime()) / 86_400_000);
  return Math.max(0, diff + 1);
}

export function computeStreak(live: readonly AppEntry[], now = new Date()): number {
  const days = new Set(live.map(shippedDay));
  if (days.size === 0) return 0;

  const cursor = startOfLocalDay(now);
  if (!days.has(toDayString(cursor))) {
    cursor.setDate(cursor.getDate() - 1);
  }

  let streak = 0;
  while (days.has(toDayString(cursor))) {
    streak += 1;
    cursor.setDate(cursor.getDate() - 1);
  }
  return streak;
}

export function computeProgress(apps: readonly AppEntry[], now = new Date()): Progress {
  const live = apps.filter(isLive);
  const count = live.length;
  const remaining = Math.max(0, QUEST_GOAL - count);
  const daysLeft = daysRemainingInclusive(now);
  const percent = (count / QUEST_GOAL) * 100;
  const needPerDay = daysLeft === 0 ? remaining : remaining / daysLeft;
  const today = toDayString(now);
  const shippedToday = live.filter((app) => shippedDay(app) === today).length;
  const streak = computeStreak(live, now);

  let actualPerDay: number | null = null;
  let paceStatus: PaceStatus;

  if (count >= QUEST_GOAL) {
    paceStatus = "complete";
  } else if (count === 0) {
    paceStatus = "empty";
  } else {
    const first = [...live.map(shippedDay)].sort()[0];
    if (!first) {
      paceStatus = "empty";
    } else {
      const parts = first.split("-").map(Number);
      const y = parts[0] ?? 2026;
      const m = parts[1] ?? 1;
      const d = parts[2] ?? 1;
      const firstDate = new Date(y, m - 1, d);
      const elapsed = Math.max(
        1,
        Math.round((startOfLocalDay(now).getTime() - firstDate.getTime()) / 86_400_000) + 1,
      );
      actualPerDay = count / elapsed;
      if (actualPerDay >= needPerDay * 1.1) paceStatus = "ahead";
      else if (actualPerDay >= needPerDay) paceStatus = "on_pace";
      else paceStatus = "behind";
    }
  }

  return {
    count,
    remaining,
    percent,
    daysLeft,
    needPerDay,
    actualPerDay,
    shippedToday,
    streak,
    paceStatus,
    live,
  };
}
